import React, { useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { RotateCcw as ResetIcon, AlertTriangle, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import ChatInput from "./ChatInput";
import ThinkingIndicator from "./ThinkingIndicator";
import MessageContent from "./MessageContent";
import { useSonas } from "../SonasContext";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import ErrorBoundary from "@/components/ui/error-boundary";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ChatPanelContent = () => {
  const { projectId } = useParams();
  const { 
    conversations = {}, 
    activeConversationId, 
    setActiveConversationId,
    setIsThinking, 
    isThinking, 
    fetchMessages,
    onSend,
    accumulatedText
  } = useSonas();
  
  const scrollRef = useRef(null);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = React.useState(false);
  
  // Robust synchronization of project messages when URL changes
  useEffect(() => {
    if (projectId) {
      setActiveConversationId(projectId);
      fetchMessages(projectId);
    }
  }, [projectId, setActiveConversationId, fetchMessages]);

  const activeConv = useMemo(() => conversations?.[projectId] || null, [conversations, projectId]);
  const messages = useMemo(() => activeConv?.messages || [], [activeConv]);

  useEffect(() => { 
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isThinking, accumulatedText]);

  const handleContextReset = async () => {
    if (!projectId || isThinking) return;
    try {
      await supabase.from('messages').delete().eq('project_id', projectId);
      fetchMessages(projectId);
      setIsResetConfirmOpen(false);
      toast.success("Project context cleared.");
    } catch (err) {
      toast.error("Reset failed.");
    }
  };

  const handleShare = () => {
    if (!activeConv?.share_id) {
      toast.error("Project has no share ID.");
      return;
    }
    const shareUrl = `${window.location.origin}/share/${activeConv.share_id}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Public share link copied to clipboard", {
      icon: <Share2 className="h-4 w-4" />
    });
  };

  const handleExport = () => {
    if (messages.length === 0) {
      toast.error("Nothing to export.");
      return;
    }
    
    const content = messages.map(m => `### ${m.role === 'user' ? 'Creator' : 'Sonas AI'}\n\n${m.text}\n\n---`).join('\n\n');
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeConv?.name || 'sonas-project'}-export.md`;
    a.click();
    toast.success("Architectural plan exported as Markdown");
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-sonas-bg">
      <header className="h-[52px] border-b border-white/[0.04] px-4 md:px-6 flex items-center justify-between bg-sonas-bg/50 backdrop-blur-xl z-20">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="h-2 w-2 rounded-full bg-sonas-accent shadow-[0_0_10px_rgba(22,163,74,0.4)]" />
          <span className="text-xs md:text-[13px] font-bold font-syne tracking-tight truncate max-w-[120px] sm:max-w-[200px]">{activeConv?.name || "Sonas Architect"}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
           <button 
             onClick={handleShare}
             className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-xl text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-sonas-dim hover:text-sonas-accent transition-all"
           >
             <Share2 size={12} className="md:h-3.5 md:w-3.5" /> <span className="hidden xs:inline">Share</span>
           </button>
           <button 
             onClick={handleExport}
             className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-widest text-sonas-dim hover:text-blue-400 transition-all"
           >
             <Download size={14} /> Export
           </button>
           <button 
             onClick={() => setIsResetConfirmOpen(true)} 
             className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-xl text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-sonas-dim hover:text-red-400 transition-all"
           >
             <ResetIcon size={12} className="md:h-3.5 md:w-3.5" /> <span className="hidden xs:inline">Reset</span>
           </button>
        </div>
      </header>
      
      <div ref={scrollRef} className="flex-1 overflow-y-auto sonas-scrollbar">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12 w-full flex flex-col gap-6">
          {messages.map((m, idx) => (
            <article key={m.id || idx} className={cn("flex flex-col gap-2", m.role === "user" ? "items-end" : "items-start")}>
              {m.imageUrl && <img src={m.imageUrl} className="max-h-[200px] md:max-h-[300px] w-auto mb-2 rounded-2xl border border-white/5" alt="Context" />}
              <MessageContent id={m.id} text={m.text || ""} role={m.role} onAction={() => {}} onEdit={() => {}} />
            </article>
          ))}
          {isThinking && (
            <div className="flex flex-col gap-4 items-start w-full">
              <ThinkingIndicator />
              {accumulatedText && <MessageContent text={accumulatedText} role="assistant" />}
            </div>
          )}
        </div>
      </div>
      <div className="max-w-4xl mx-auto w-full"><ChatInput onSend={onSend} isThinking={isThinking} /></div>

      <AlertDialog open={isResetConfirmOpen} onOpenChange={setIsResetConfirmOpen}>
        <AlertDialogContent className="glass-surface max-w-sm rounded-[32px] p-8 border-none ring-0">
          <AlertDialogHeader>
            <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <AlertDialogTitle className="text-2xl font-syne text-center">Clear History?</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-sm text-sonas-muted leading-relaxed">
              This will permanently erase all architectural context from this project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-col gap-3 mt-6">
            <AlertDialogAction onClick={handleContextReset} className="w-full bg-red-500 hover:bg-red-600 text-white font-bold rounded-2xl h-12 shadow-lg shadow-red-500/20 border-none transition-all">Confirm Reset</AlertDialogAction>
            <AlertDialogCancel className="w-full rounded-2xl bg-sonas-surface2/50 border-none h-12 text-sonas-text font-semibold hover:bg-sonas-surface2 transition-all">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ChatPanel = () => (
  <ErrorBoundary>
    <ChatPanelContent />
  </ErrorBoundary>
);

export default ChatPanel;